/**
 * MobileTabBarV2 — v2 mobile tab bar (4 tabs + More)
 * Preserved for FBR builds. v3 uses BottomSheetTabs instead.
 */

import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  FileText,
  AlertTriangle,
  ClipboardCheck,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '../../utils/cn';

interface TabItem {
  key: string;
  label: string;
  icon: typeof BarChart3;
  path: string;
}

const TABS: TabItem[] = [
  { key: 'dashboard', label: 'Home', icon: BarChart3, path: '/dashboard' },
  { key: 'policies', label: 'Policies', icon: FileText, path: '/policies' },
  { key: 'findings', label: 'Findings', icon: AlertTriangle, path: '/findings' },
  { key: 'inspections', label: 'Inspections', icon: ClipboardCheck, path: '/inspections' },
];

export function MobileTabBarV2() {
  const location = useLocation();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav
      data-testid="mobile-tab-bar"
      data-version="v2"
      aria-label="Mobile navigation"
      className="md:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 dark:bg-slate-900 dark:border-slate-800 z-40"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-center justify-around h-14">
        {TABS.map((tab) => {
          const active = isActive(tab.path);
          return (
            <Link
              key={tab.key}
              to={tab.path}
              data-testid={`tab-${tab.key}`}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full gap-0.5 min-w-[44px] min-h-[44px]',
                active ? 'text-gray-500 dark:text-slate-300' : 'text-gray-400 dark:text-slate-500',
              )}
              aria-current={active ? 'page' : undefined}
            >
              <tab.icon size={20} />
              <span className="text-[11px] font-medium leading-tight">{tab.label}</span>
            </Link>
          );
        })}

        <Link
          to="/more"
          data-testid="tab-more"
          className={cn(
            'flex flex-col items-center justify-center flex-1 h-full gap-0.5 min-w-[44px] min-h-[44px]',
            isActive('/more') || isActive('/settings') ? 'text-gray-500 dark:text-slate-300' : 'text-gray-400 dark:text-slate-500',
          )}
          aria-label="More navigation options"
        >
          <MoreHorizontal size={20} />
          <span className="text-[11px] font-medium leading-tight">More</span>
        </Link>
      </div>
    </nav>
  );
}
